import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, DollarSign, Zap } from 'lucide-react';

const FastTrackYear = () => {
  // Define a common animation for the icons (rolling)
  const iconRollAnimation = {
    rotateY: 360, // Rotate around the Y-axis for a left-to-right roll
  };

  // Define a common transition for the icons (rolling)
  const iconRollTransition = {
    duration: 15, // 15 seconds for one full rotation (slow)
    ease: "linear", // Constant speed
    repeat: Infinity, // Loop indefinitely
  };

  // Define colors for the icon's neon glow effect and color change
  const iconNeonColors = [
    "#00FFFF", // Cyan
    "#FF00FF", // Magenta
    "#FFFF00", // Yellow
    "#00FF00", // Green
    "#0080FF", // Blue
    "#FF8000", // Orange
  ];

  // Define animation for the icon's color and glow
  const iconColorAnimation = {
    color: iconNeonColors, // Animate the icon's color
    boxShadow: iconNeonColors.map(color => `0 0 10px ${color}, 0 0 20px ${color}, 0 0 30px ${color}`), // Animate box-shadow for glow
  };

  const iconColorTransition = {
    duration: 25, // Very slow transition for color change
    ease: "easeInOut",
    repeat: Infinity,
    repeatType: "mirror", // Go back and forth through colors
  };

  // Define colors for the main heading text animation
  const headingTextColors = [
    "#80FFFF", // Light Cyan
    "#FF80FF", // Light Magenta
    "#FFFF80", // Light Yellow
    "#80FF80", // Light Green
    "#80BFFF", // Light Blue
    "#FFC080", // Light Orange
  ];

  // Define animation for the main heading text color
  const headingColorAnimation = {
    color: headingTextColors, // Animate through these colors
  };

  const headingColorTransition = {
    duration: 30, // Very slow transition over 30 seconds
    ease: "linear", // Smooth, continuous change
    repeat: Infinity, // Loop indefinitely
    repeatType: "mirror", // Go back and forth through the colors
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.8 }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 text-center" // Changed background to dark gray
    >
      <motion.h2
        className="text-3xl md:text-4xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400" // Keep gradient for initial look, but animate color
        animate={headingColorAnimation} // Apply color animation
        transition={headingColorTransition} // Apply color transition
      >
        Go Further with the Fast-Track Year
      </motion.h2>
      <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
        Commit to a full year of financial transformation and unlock even greater savings and benefits.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="relative bg-gray-900/50 p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center" // Added relative for glow positioning
        >
          {/* Neon Glow Effect for each feature card */}
          <motion.div
            className="absolute inset-0 rounded-2xl" // No negative margin, just inset
            style={{
              background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', // Pearl white gradient
              filter: 'blur(8px)', // Soft glow
              zIndex: -1, // Behind the main content
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', // Mask to create border effect
              WebkitMaskComposite: 'xor', // Combine masks
              maskComposite: 'exclude', // Standard for exclude
            }}
            animate={{ opacity: [0.2, 0.5, 0.2] }} // Slow flicker
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mb-4"
            animate={{ ...iconRollAnimation, ...iconColorAnimation }} // Combine roll and color animations
            transition={{ ...iconRollTransition, ...iconColorTransition }} // Combine roll and color transitions
          >
            <Rocket size={48} /> {/* Lucide icons use 'color' for stroke/fill */}
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">Accelerated Progress</h3>
          <p className="text-gray-300">Achieve your credit goals faster with consistent, long-term support.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="relative bg-gray-900/50 p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center"
        >
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mb-4"
            animate={{ ...iconRollAnimation, ...iconColorAnimation }} // Combine roll and color animations
            transition={{ ...iconRollTransition, ...iconColorTransition }} // Combine roll and color transitions
          >
            <DollarSign size={48} />
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">Significant Savings</h3>
          <p className="text-300">Enjoy a reduced monthly rate compared to our month-to-month plans.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="relative bg-gray-900/50 p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center"
        >
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mb-4"
            animate={{ ...iconRollAnimation, ...iconColorAnimation }} // Combine roll and color animations
            transition={{ ...iconRollTransition, ...iconColorTransition }} // Combine roll and color transitions
          >
            <Zap size={48} />
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">Exclusive Benefits</h3>
          <p className="text-gray-300">Access premium content and priority support for a seamless journey.</p>
        </motion.div>
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
      >
        Unlock Your Fast-Track Year
      </motion.button>
    </motion.div>
  );
};

export default FastTrackYear;